import { useState, type FormEvent } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../auth/AuthContext";
import { agentSeatErrorMessage } from "../api/extensions";
import type { SeatChoice } from "../api/auth";

/**
 * The agent's own sign-in page — deliberately separate from the operator
 * LoginPage (Resolved Decisions #2, 2026-07-30 agent-logins plan: agents
 * never use the operator form). Two steps:
 *
 *   1. Username + password, against POST /auth/agent-login. A bound agent
 *      gets a token immediately; an unbound one gets a seat challenge plus
 *      their operator's live web seats.
 *   2. A seat picker for the unbound case. Picking a seat that's already
 *      claimed comes back as a 409 with a message this page shows verbatim
 *      — the challenge deliberately survives that (the backend does not burn
 *      it on a claimed-seat conflict), so the agent can just pick another one
 *      from the same list without re-entering their password.
 */
export function AgentLoginPage() {
  const { signInAgent, selectSeat } = useAuth();
  const navigate = useNavigate();

  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const [seatChallenge, setSeatChallenge] = useState<string | null>(null);
  const [seats, setSeats] = useState<SeatChoice[]>([]);
  const [pickingSeat, setPickingSeat] = useState<number | null>(null);

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    setBusy(true);
    try {
      const result = await signInAgent(username, password);
      if (result.requiresSeat && result.seatChallenge) {
        setSeatChallenge(result.seatChallenge);
        setSeats(result.seats ?? []);
      } else {
        navigate("/agent", { replace: true });
      }
    } catch (err) {
      // Backend detail strings are already written for an agent to read
      // verbatim: "Invalid credentials", "Too many failed sign-in attempts.
      // Try again in 15 minutes.", "No agent seats are available on this
      // account. Ask your manager to add one." — no need to re-derive them
      // from the HTTP status here.
      setError(agentSeatErrorMessage(err));
    } finally {
      setBusy(false);
    }
  }

  async function handlePickSeat(extensionId: number) {
    if (!seatChallenge) return;
    setError(null);
    setPickingSeat(extensionId);
    try {
      await selectSeat(seatChallenge, extensionId);
      navigate("/agent", { replace: true });
    } catch (err) {
      // A 409 ("Someone is already signed in on that seat. Pick another
      // one.") leaves the challenge and seat list exactly as they are — the
      // agent can just tap a different seat. An expired/exhausted challenge
      // (401/429) has no seat list left to retry against, so send them back
      // to the start.
      const message = agentSeatErrorMessage(err);
      setError(message);
      const status = (err as { response?: { status?: number } })?.response?.status;
      if (status === 401 || status === 429) {
        setSeatChallenge(null);
        setSeats([]);
      }
    } finally {
      setPickingSeat(null);
    }
  }

  function backToSignIn() {
    setSeatChallenge(null);
    setSeats([]);
    setError(null);
  }

  if (seatChallenge) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-950 px-4">
        <div className="bg-white rounded-2xl shadow-xl p-8 w-full max-w-sm space-y-4">
          <div className="text-center">
            <h1 className="text-xl font-bold text-brand">Which station are you on today?</h1>
            <p className="text-sm text-gray-500 mt-1">Pick your seat to start your shift.</p>
          </div>
          {error && (
            <div role="alert" className="bg-red-50 text-red-700 text-sm rounded px-3 py-2">
              {error}
            </div>
          )}
          <div className="space-y-2">
            {seats.map((seat) => (
              <button
                key={seat.extension_id}
                type="button"
                disabled={pickingSeat !== null}
                onClick={() => void handlePickSeat(seat.extension_id)}
                className="w-full text-left border border-gray-200 rounded-xl px-4 py-3 hover:border-brand hover:bg-brand/5 transition disabled:opacity-50 disabled:cursor-wait"
              >
                <div className="font-semibold text-gray-900">
                  {seat.title || `Seat ${seat.name}`}
                </div>
                <div className="text-xs text-gray-500">
                  {pickingSeat === seat.extension_id ? "Signing in…" : `Extension ${seat.name}`}
                </div>
              </button>
            ))}
          </div>
          <button
            type="button"
            onClick={backToSignIn}
            className="w-full text-sm text-gray-500 hover:text-gray-700"
          >
            Back to sign in
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-950 px-4">
      <form
        onSubmit={handleSubmit}
        className="bg-white rounded-2xl shadow-xl p-8 w-full max-w-sm space-y-4"
      >
        <div className="text-center">
          <div className="inline-flex h-11 w-11 items-center justify-center rounded-xl bg-brand text-white font-bold text-lg mb-2">
            R
          </div>
          <h1 className="text-xl font-bold text-brand">Agent sign-in</h1>
          <p className="text-sm text-gray-500 mt-1">Reliadata call centre console</p>
        </div>
        {error && (
          <div role="alert" className="bg-red-50 text-red-700 text-sm rounded px-3 py-2">
            {error}
          </div>
        )}
        <div>
          <label htmlFor="agent-username" className="block text-sm text-gray-600 mb-1">
            Username
          </label>
          <input
            id="agent-username"
            className="w-full border border-gray-300 rounded px-3 py-2"
            value={username}
            onChange={(e) => setUsername(e.target.value)}
            autoComplete="username"
            autoFocus
            required
          />
        </div>
        <div>
          <label htmlFor="agent-password" className="block text-sm text-gray-600 mb-1">
            Password
          </label>
          <input
            id="agent-password"
            type="password"
            className="w-full border border-gray-300 rounded px-3 py-2"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            autoComplete="current-password"
            required
          />
        </div>
        <button
          type="submit"
          disabled={busy}
          className="w-full bg-brand hover:bg-brand-dark text-white rounded py-2 disabled:opacity-50"
        >
          {busy ? "Signing in…" : "Sign in"}
        </button>
        <p className="text-center text-xs text-gray-400">
          Are you a manager or supervisor? Sign in at the{" "}
          <a href="/login" className="text-brand hover:underline">
            operator portal
          </a>
          .
        </p>
      </form>
    </div>
  );
}
